import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputBase,
  TextField,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SearchIcon from "@material-ui/icons/Search";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import { formatarDuracao } from "../../pages/Hotspot/helpers";
import NewBatchModal from "../../pages/Hotspot/NewBatchModal";
import useDialogStyles from "../TransferTicketModalCustom/dialogStyles";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  // mesmos tons da busca do painel de conversas (padrão do WhatsApp Web)
  const wa = isDark
    ? { field: "#202c33", focus: "#00a884" }
    : { field: "#f0f2f5", focus: "#00a884" };
  return {
    searchBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: 38,
      padding: "0 12px 0 14px",
      borderRadius: 19,
      backgroundColor: wa.field,
      border: "1px solid transparent",
      flex: "none",
      "&:focus-within": {
        backgroundColor: t.paper,
        borderColor: wa.focus,
        boxShadow: `0 0 0 1px ${wa.focus}`,
      },
    },
    searchIcon: {
      color: t.textMuted,
      fontSize: 18,
      flex: "none",
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: 13.5,
      color: t.text,
      "& input::placeholder": {
        color: t.textMuted,
        opacity: 1,
      },
    },
    list: {
      maxHeight: 280,
      overflowY: "auto",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      ...theme.scrollbarStyles,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      padding: "9px 12px",
      border: 0,
      borderTop: `1px solid ${t.lineRow}`,
      background: "transparent",
      color: t.text,
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      transition: "background-color 120ms ease",
      "&:first-child": { borderTop: 0 },
      "&:hover": { backgroundColor: t.muted },
      "&:focus-visible": {
        outline: `2px solid ${wa.focus}`,
        outlineOffset: -2,
      },
    },
    rowText: {
      flex: 1,
      minWidth: 0,
    },
    rowName: {
      display: "block",
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "19px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rowMeta: {
      display: "block",
      fontSize: 12.5,
      lineHeight: "17px",
      color: t.textSecondary,
    },
    rowArrow: {
      flex: "none",
      fontSize: 20,
      color: t.textMuted,
    },
    createRow: {
      display: "flex",
      justifyContent: "center",
    },
    selectedBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
    },
    resultBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      textAlign: "center",
    },
    successIcon: {
      fontSize: 40,
      color: t.semantic.success,
    },
  };
});

// Dialog used to send a hotspot voucher batch to a customer over WhatsApp.
//
// - Without `batch`: shows a search box + list of released ("liberado")
//   batches (GET /hotspot/voucher-batches?q=&take=20), plus a "Criar lote
//   agora" button that reuses NewBatchModal; on success the created batch is
//   selected automatically.
// - With `batch`: skips the picker and sends that batch directly.
// - With `ticketId`: sends to that ticket (POST .../send { ticketId }).
// - Without `ticketId`: asks for the customer's WhatsApp number and sends
//   with { number } (backend finds/creates the contact + a closed ticket).
const SendVoucherDialog = ({ open, onClose, ticketId, batch }) => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles({ width: 420 });

  const [selectedBatch, setSelectedBatch] = useState(batch || null);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [devices, setDevices] = useState([]);

  const [number, setNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedBatch(batch || null);
      setQ("");
      setList([]);
      setNewBatchOpen(false);
      setNumber("");
      setSending(false);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch]);

  const fetchBatches = useCallback(async (query) => {
    setListLoading(true);
    try {
      const { data } = await api.get("/hotspot/voucher-batches", {
        params: { q: query || undefined, take: 20 },
      });
      if (!isMountedRef.current) return;
      const batches = Array.isArray(data?.batches) ? data.batches : [];
      setList(batches.filter((b) => String(b?.status || "").toLowerCase() === "liberado"));
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !batch && !selectedBatch) {
      fetchBatches(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch, selectedBatch, q]);

  useEffect(() => {
    if (newBatchOpen && devices.length === 0) {
      (async () => {
        try {
          const { data } = await api.get("/hotspot/devices");
          if (isMountedRef.current) setDevices(Array.isArray(data) ? data : []);
        } catch (err) {
          // silent: device select just falls back to "integration default"
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBatchOpen]);

  const handleBatchCreated = (created) => {
    if (created?.id) {
      setSelectedBatch(created);
      setNewBatchOpen(false);
    }
  };

  const digits = number.replace(/\D/g, "");
  const numberValid = digits.length >= 10 && digits.length <= 13;
  const canSend = !!selectedBatch?.id && (!!ticketId || numberValid);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const body = ticketId ? { ticketId } : { number: digits };
      const { data } = await api.post(`/hotspot/voucher-batches/${selectedBatch.id}/send`, body);
      if (!isMountedRef.current) return;
      setResult(data);
      toast.success(i18n.t("hotspot.toasts.sent"));
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const showPicker = !batch && !selectedBatch && !result;
  const showConfirm = !!selectedBatch && !result;

  const batchMeta = (b) =>
    `${formatarDuracao(b.duracaoMin)} · ${Number(b?.contadores?.disponivel) || 0} ${i18n.t(
      "hotspot.totals.available"
    )}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        classes={{ paper: dialogClasses.paper }}
        aria-labelledby="send-voucher-title"
      >
        <div className={dialogClasses.header}>
          <h2 id="send-voucher-title" className={dialogClasses.title}>
            {i18n.t("hotspot.send.title")}
          </h2>
        </div>
        <DialogContent className={dialogClasses.content}>
          {result ? (
            <div className={`${dialogClasses.infoBox} ${classes.resultBox}`}>
              <CheckCircleIcon className={classes.successIcon} />
              <span>
                {result.mode === "pdf"
                  ? i18n.t("hotspot.send.resultPdf")
                  : i18n.t("hotspot.send.resultLink")}
              </span>
            </div>
          ) : showPicker ? (
            <>
              <div className={classes.searchBox}>
                <SearchIcon className={classes.searchIcon} />
                <InputBase
                  className={classes.searchInput}
                  placeholder={i18n.t("hotspot.send.searchPlaceholder")}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  autoFocus
                  inputProps={{ "aria-label": i18n.t("hotspot.send.searchPlaceholder") }}
                />
              </div>
              <div className={classes.list}>
                {listLoading ? (
                  <div className={dialogClasses.loading}>
                    <CircularProgress size={18} color="inherit" />
                  </div>
                ) : list.length === 0 ? (
                  <div className={dialogClasses.empty}>
                    {i18n.t("hotspot.send.empty")}
                  </div>
                ) : (
                  list.map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      className={classes.row}
                      onClick={() => setSelectedBatch(b)}
                    >
                      <span className={classes.rowText}>
                        <span className={classes.rowName}>{b.nome}</span>
                        <span className={classes.rowMeta}>{batchMeta(b)}</span>
                      </span>
                      <ChevronRightIcon className={classes.rowArrow} />
                    </button>
                  ))
                )}
              </div>
              <div className={classes.createRow}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setNewBatchOpen(true)}
                >
                  {i18n.t("hotspot.send.createNow")}
                </Button>
              </div>
            </>
          ) : showConfirm ? (
            <>
              <div className={`${dialogClasses.infoBox} ${classes.selectedBox}`}>
                <div className={classes.rowText}>
                  <span className={classes.rowName}>{selectedBatch.nome}</span>
                  <span className={classes.rowMeta}>{batchMeta(selectedBatch)}</span>
                </div>
                {!batch && (
                  <Tooltip title={i18n.t("hotspot.send.change")}>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedBatch(null)}
                      aria-label={i18n.t("hotspot.send.change")}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>

              {ticketId ? (
                <span className={dialogClasses.hint}>
                  {i18n.t("hotspot.send.toTicket")}
                </span>
              ) : (
                <div className={dialogClasses.field}>
                  <span className={dialogClasses.label}>
                    {i18n.t("hotspot.send.numberLabel")}
                  </span>
                  <TextField
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="51999998888"
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoFocus
                    disabled={sending}
                    error={number.length > 0 && !numberValid}
                    helperText={number.length > 0 && !numberValid ? i18n.t("hotspot.send.numberInvalid") : " "}
                    inputProps={{ "aria-label": i18n.t("hotspot.send.numberLabel") }}
                  />
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          {result ? (
            <Button onClick={onClose} color="primary" variant="contained" className={dialogClasses.confirm}>
              {i18n.t("hotspot.result.done")}
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={sending} className={dialogClasses.cancel}>
                {i18n.t("hotspot.buttons.cancel")}
              </Button>
              {showConfirm && (
                <Button
                  onClick={handleSend}
                  color="primary"
                  variant="contained"
                  disabled={sending || !canSend}
                  className={dialogClasses.confirm}
                >
                  {i18n.t("hotspot.buttons.send")}
                  {sending && <CircularProgress size={18} color="inherit" style={{ marginLeft: 8 }} />}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <NewBatchModal
        open={newBatchOpen}
        devices={devices}
        onClose={() => setNewBatchOpen(false)}
        onCreated={handleBatchCreated}
      />
    </>
  );
};

export default SendVoucherDialog;
